<script setup lang="ts">
import { MenuIcon as Bars3Icon } from "@heroicons/vue/outline"
import { Flash, Spinner } from "@xy-planning-network/trees"
import { computed, onMounted, ref, useTemplateRef } from "vue"
import DisclosureSearch from "./DisclosureSearch.vue"
import Prose from "@/components/shared/Prose.vue"
import FacebookIcon from "@/components/find-an-advisor/shared/IconFacebook.vue"
import TwitterIcon from "@/components/find-an-advisor/shared/IconTwitter.vue"
import LinkedInIcon from "@/components/find-an-advisor/shared/IconLinkedin.vue"
import { router } from "@/routes"
import AppLayoutSlideover from "@/components/find-an-advisor/shared/AppLayoutSlideover.vue"

interface NavItem {
  current: boolean
  name: string
  openInTab?: boolean
  url: string
}

const props = defineProps<{
  onFaaBtnClick?: () => void
}>()

// The XYPN advice subdomain url.
const adviceUrl = "https://advice.xyplanningnetwork.com"

const currentPath = window.location.pathname || "/"

const navigation = computed((): NavItem[] => {
  return [
    // Add Nav Items for header nav as needed
    // Desktop Nav is right aligned in the header
    {
      name: "Home",
      url: adviceUrl,
      openInTab: false,
    },
    {
      name: "Good Financial Reads",
      url: `${adviceUrl}/consumer-blog`,
      openInTab: false,
    },
  ].map((navItem) => {
    return {
      ...navItem,
      current: navItem.url === currentPath,
    }
  })
})

const handleFaaBtnClick = () => {
  if (props.onFaaBtnClick) {
    if (showSlideoverNav.value) showSlideoverNav.value = false
    props.onFaaBtnClick()
    return
  }

  window.location.href = router.FindAdvisors()
}

const showSlideoverNav = ref(false)

// css animations trigger as early as first load
// use this ref to avoid applying animation classes until
// the first sticky event occurs
const startSticky = ref(false)
// isSticky - the current state of the sticky nav
const isSticky = ref(false)
// the primary nav/header container to reference for sticky mode
const navRef = useTemplateRef("nav")
// used to maintain overall site height when entering sticky mode
const navHeight = ref<number | null>(null)

// shouldStick determines if the page position has
// scrolled past the height of the primary nav container
const shouldStick = () => {
  if (navRef.value === null) {
    return
  }

  const height = navRef.value.offsetHeight

  if (window.scrollY >= height) {
    startSticky.value = true
    isSticky.value = true
    // NOTE(spk): only set the height when entering sticky mode
    navHeight.value = height
  } else {
    isSticky.value = false
  }
}

// NOTE(spk): I would typically throttle a scroll event
// but the nav height is so short that it's not uncommon for
// a throttled event to be a few pixels short preventing it
// from removing sticky mode.
window.onscroll = shouldStick

// NOTE(spk): May need to sticky the nav if an anchor is present
onMounted(() => setTimeout(shouldStick, 100))
</script>

<template>
  <div class="min-h-full bg-white antialiased">
    <div ref="nav" :style="isSticky ? { height: `${navHeight}px` } : null">
      <div :class="isSticky ? 'fixed w-full top-0 left-0 z-20 shadow' : ''">
        <nav
          :class="[
            'bg-white',
            startSticky
              ? isSticky
                ? 'faa-sticky-nav-active'
                : 'faa-sticky-nav-inactive'
              : '',
          ]"
        >
          <div
            :class="[
              'faa-content',
              isSticky ? 'py-2.5' : 'py-[14px] xl:py-[18px]',
            ]"
          >
            <div class="flex -mx-3">
              <div class="px-3 flex flex-col justify-center w-2/5 md:w-1/4">
                <a :href="adviceUrl">
                  <img
                    :class="['h-auto w-[165px]', isSticky ? 'hidden' : 'block']"
                    src="@/assets/faa-nav-logo.png"
                    alt="XY Planning Network Logo"
                  />

                  <img
                    :class="['h-auto w-[55px]', isSticky ? 'block' : 'hidden']"
                    src="@/assets/xypn-logo-mark.png"
                    alt="XY Planning Network Logo Mark"
                  />
                </a>
              </div>

              <div class="flex-1 px-3 flex items-center justify-end">
                <!--Desktop Primary Navigation-->
                <div
                  v-if="navigation.length > 0"
                  class="hidden sm:-my-px sm:pl-6 xl:flex sm:space-x-8"
                >
                  <a
                    v-for="item in navigation"
                    :key="item.name"
                    :href="item.url"
                    class="text-gray-800 group font-normal flex flex-col"
                    :aria-current="item.current ? 'page' : undefined"
                    ><span class="py-3">{{ item.name }}</span>
                    <!--Fancy border active state and hover effect-->
                    <span
                      :class="[
                        'h-[3px] -mt-1.5 rounded-full w-full bg-xy-blue',
                        item.current
                          ? 'opacity-100 scale-x-100 translate-y-0'
                          : 'translate-y-0.5 scale-x-[.2] opacity-0 transition-all ease-out duration-500 group-hover:opacity-100 group-hover:scale-x-100 group-hover:translate-y-0',
                      ]"
                      aria-hidden
                  /></a>
                </div>

                <!--Desktop Find An Advisor CTA-->
                <div class="hidden md:block ml-9">
                  <button class="xy-btn" @click="handleFaaBtnClick">
                    Find an Advisor →
                  </button>
                </div>

                <!--Mobile Navigation Burger-->
                <div class="-mr-2.5 ml-3 flex items-center xl:hidden">
                  <button
                    class="relative inline-flex items-center justify-center rounded-md p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-xy-blue focus:ring-offset-2"
                    @click="showSlideoverNav = true"
                  >
                    <span class="absolute -inset-0.5" />
                    <span class="sr-only">Open main menu</span>
                    <Bars3Icon class="block h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>

    <!-- Mobile Nav Slideover-->
    <AppLayoutSlideover v-model="showSlideoverNav">
      <nav>
        <a
          v-for="item in navigation"
          :key="item.name"
          :href="item.url"
          :class="[
            item.current ? '' : '',
            'block px-6 py-3.5 font-base font-normal text-gray-800 border-t border-gray-200 first:border-none',
          ]"
          :aria-current="item.current ? 'page' : undefined"
          >{{ item.name }}</a
        >

        <!--Slideover Find An Advisor CTA-->
        <div class="p-6 border-b border-gray-200">
          <button class="xy-btn" @click="handleFaaBtnClick">
            Find an Advisor →
          </button>
        </div>
      </nav>
    </AppLayoutSlideover>

    <div>
      <header v-if="$slots['banner']">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <slot name="banner" />
        </div>
      </header>
      <main>
        <!-- Your content -->
        <slot />
      </main>
      <footer>
        <div class="bg-xy-black py-24 text-white">
          <div class="faa-content">
            <div class="lg:flex">
              <div
                class="mb-12 lg:mb-auto lg:max-w-lg lg:border-r lg:border-neutral-700 lg:pr-14 lg:mr-14"
              >
                <h3 class="text-3xl leading-8 font-extrabold font-display mb-8">
                  XYPN Makes it Possible
                </h3>
                <p class="text-lg mb-4">
                  XYPN is the only advisor support platform that makes it
                  possible for fee-only financial planners to build the
                  independent firm of their dreams with complete autonomy. We do
                  this through ongoing advocacy and access to education and
                  training, curated technology, compliance support, like-minded
                  community, and back-office and client support services.
                </p>
                <a
                  class="xy-btn-accent-inverse-lg"
                  href="https://www.xyplanningnetwork.com/contact-us"
                  >Get in touch &xrarr;</a
                >
                <ul class="flex gap-6 mt-6">
                  <li class="h-8 w-8">
                    <a href="https://x.com/xypnofficial" target="_blank">
                      <span class="sr-only">XYPN Twitter Link</span>
                      <component :is="TwitterIcon" class="fill-current" />
                    </a>
                  </li>
                  <li class="h-8 w-8">
                    <a
                      href="https://www.facebook.com/xyplanningnetwork"
                      target="_blank"
                    >
                      <span class="sr-only">XYPN Facebook Link</span>
                      <component :is="FacebookIcon" class="fill-current" />
                    </a>
                  </li>
                  <li class="h-8 w-8">
                    <a
                      href="https://www.linkedin.com/company/xypn/"
                      target="_blank"
                    >
                      <span class="sr-only">XYPN Facebook Link</span>
                      <component :is="LinkedInIcon" class="fill-current" />
                    </a>
                  </li>
                </ul>
              </div>
              <div class="flex-1">
                <h4
                  class="mb-3 uppercase text-base leading-4 font-display font-extrabold text-neutral-50"
                >
                  Disclosure
                </h4>
                <Prose size="sm" class="text-neutral-50 faa-disclosure">
                  <slot name="disclosure">
                    <DisclosureSearch />
                  </slot>
                </Prose>
              </div>
            </div>
            <div class="border-t border-neutral-700 pt-7 mt-12">
              <p class="text-lg">
                Copyright © {{ new Date().getFullYear() }} XYPN
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>

  <Flash />
  <Spinner />
</template>
